import { Link, Navigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import {
  ChevronRight, Sparkles, Shield, Clock, Star, Globe, Sun, Moon,
  MessageCircle, ArrowRight, Check,
} from 'lucide-react';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1620694655075-6ed740167a66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHx0dXJxdW9pc2UlMjBjYXJpYmJlYW4lMjBiZWFjaCUyMGFlcmlhbCUyMHZpZXclMjBtZXhpY298ZW58MHx8fHwxNzc0MDIxMzk1fDA&ixlib=rb-4.1.0&q=85';

export default function LandingPage() {
  const { language, toggleLanguage } = useLanguage();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // If logged in → straight to mobile app
  if (user) return <Navigate to="/dashboard" replace />;

  const features = [
    {
      icon: Clock,
      title_en: 'Real-time offers',
      title_es: 'Ofertas en tiempo real',
      desc_en: 'As soon as you post, certified local providers compete to send you offers. Choose your favorite within 12 hours.',
      desc_es: 'Apenas publicas, proveedores locales certificados compiten con ofertas. Elige tu favorita dentro de 12 horas.',
    },
    {
      icon: Shield,
      title_en: 'Reserve with just 17% + IVA',
      title_es: 'Aparta con 17% + IVA',
      desc_en: 'Pay only the reservation fee online. Pay the rest in cash directly to your provider on arrival.',
      desc_es: 'Paga solo el anticipo online. El resto se lo pagas en efectivo al proveedor al llegar.',
    },
    {
      icon: MessageCircle,
      title_en: 'Chuy 24/7',
      title_es: 'Chuy 24/7',
      desc_en: 'Mexican AI concierge with emergency help & tips.',
      desc_es: 'Concierge mexicano con IA, emergencias y tips.',
    },
  ];

  const howItWorks = [
    {
      n: '01',
      title_en: 'Tell us what you want',
      title_es: 'Cuéntanos qué quieres',
      desc_en: 'Yacht? Cenote tour? VIP table? Describe it in seconds.',
      desc_es: '¿Yate? ¿Cenote? ¿Mesa VIP? Descríbelo en segundos.',
    },
    {
      n: '02',
      title_en: 'Get instant offers',
      title_es: 'Recibe ofertas al instante',
      desc_en: 'You get a push the moment a certified provider sends an offer. You have 12 hours to choose.',
      desc_es: 'Te llega notificación al cel apenas un proveedor certificado mande oferta. Tienes 12 horas para elegir.',
    },
    {
      n: '03',
      title_en: 'Choose & enjoy',
      title_es: 'Elige y disfruta',
      desc_en: 'Pick the best, pay just the reservation fee, pay the rest on arrival.',
      desc_es: 'Elige la mejor, paga solo el anticipo, paga el resto al llegar.',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="landing-page">
      {/* Top nav */}
      <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl font-bold">
            Bid<span className="text-caribbean">Trip</span>
          </Link>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleLanguage}
              data-testid="lang-toggle"
              className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted"
              aria-label="lang"
            >
              <Globe size={18} />
            </button>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted"
              aria-label="theme"
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <Link to="/auth">
              <Button data-testid="login-btn" size="sm" className="ml-1 rounded-full h-9 px-4 bg-caribbean hover:bg-caribbean/90 text-white text-xs">
                {language === 'en' ? 'Sign in' : 'Iniciar sesión'}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-14 min-h-[88vh] flex items-end overflow-hidden">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-background" />

        {/* HERO content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-5 pb-12">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-caribbean bg-white/15 backdrop-blur-md rounded-full px-3 py-1 mb-4">
            <Sparkles size={12} /> {language === 'en' ? 'Mexico Caribbean · since 2026' : 'Caribe Mexicano · desde 2026'}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-4 max-w-2xl">
            {language === 'en'
              ? 'Book the experience YOU want — providers compete for it.'
              : 'Reserva la experiencia que TÚ quieres — los proveedores compiten por ti.'}
          </h1>
          <p className="text-white/85 text-base sm:text-lg max-w-xl mb-7">
            {language === 'en'
              ? "It's like inDrive — but for yachts, tours, parties, and unforgettable days in Playa, Cancún, Tulum & Los Cabos."
              : 'Es como inDrive — pero para yates, tours, fiestas y días inolvidables en Playa, Cancún, Tulum y Los Cabos.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 max-w-md">
            <Link to="/auth" className="flex-1">
              <Button
                data-testid="hero-register-btn"
                className="w-full h-12 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold shadow-xl"
              >
                {language === 'en' ? 'Create free account' : 'Crear cuenta gratis'}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/catalog" className="flex-1">
              <Button
                data-testid="hero-browse-btn"
                variant="outline"
                className="w-full h-12 rounded-full bg-white/10 backdrop-blur text-white border-white/30 hover:bg-white/20 font-semibold"
              >
                {language === 'en' ? 'Browse without account' : 'Explorar sin cuenta'}
              </Button>
            </Link>
          </div>
          <div className="mt-6 flex items-center gap-2 text-xs text-white/70">
            <Check size={14} className="text-caribbean" />
            {language === 'en' ? 'No fees to post a request · Pay only 17% + IVA to reserve' : 'Publicar es gratis · Solo pagas 17% + IVA para apartar'}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-5">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2 text-center">
            {language === 'en' ? 'Why travelers love BidTrip' : 'Por qué los viajeros aman BidTrip'}
          </h2>
          <p className="text-muted-foreground text-center mb-10 text-sm">
            {language === 'en'
              ? 'Three reasons we made tourism actually fun in México.'
              : 'Tres razones por las que hicimos el turismo divertido en México.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-2xl bg-caribbean/10 text-caribbean flex items-center justify-center mb-3">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-1">
                    {language === 'en' ? f.title_en : f.title_es}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {language === 'en' ? f.desc_en : f.desc_es}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-5 bg-muted/40">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-10 text-center">
            {language === 'en' ? 'How it works' : 'Cómo funciona'}
          </h2>
          <div className="space-y-4">
            {howItWorks.map((s) => (
              <div key={s.n} className="flex items-start gap-4 bg-card border border-border rounded-2xl p-5">
                <span className="font-heading text-3xl font-bold text-caribbean shrink-0 w-12">
                  {s.n}
                </span>
                <div>
                  <h3 className="font-heading font-bold text-lg mb-1">
                    {language === 'en' ? s.title_en : s.title_es}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'en' ? s.desc_en : s.desc_es}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-5 brand-mesh">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-1 mb-2 text-gold">
            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">
            {language === 'en' ? 'Ready for your next adventure?' : '¿Listo para tu próxima aventura?'}
          </h2>
          <p className="text-muted-foreground mb-6 text-sm">
            {language === 'en'
              ? 'Sign up free in 30 seconds. Receive your first proposals today.'
              : 'Regístrate gratis en 30 segundos. Recibe tus primeras propuestas hoy.'}
          </p>
          <Link to="/auth">
            <Button
              data-testid="cta-register-btn"
              className="h-12 px-6 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold shadow-xl"
            >
              {language === 'en' ? 'Create free account' : 'Crear cuenta gratis'}
              <ChevronRight className="ml-1" size={18} />
            </Button>
          </Link>
          <p className="mt-3 text-xs text-muted-foreground">
            {language === 'en' ? 'Are you a provider?' : '¿Eres proveedor?'}{' '}
            <Link to="/auth?role=provider" className="text-caribbean hover:underline font-medium">
              {language === 'en' ? 'Sell experiences with us' : 'Vende experiencias con nosotros'}
            </Link>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-5 py-8 border-t border-border">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="font-heading text-lg font-bold">
            Bid<span className="text-caribbean">Trip</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
            <Link to="/terms" className="hover:text-caribbean">
              {language === 'en' ? 'Terms' : 'Términos'}
            </Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-caribbean">
              {language === 'en' ? 'Privacy' : 'Privacidad'}
            </Link>
            <span>·</span>
            <Link to="/provider-terms" className="hover:text-caribbean">
              {language === 'en' ? 'Providers' : 'Proveedores'}
            </Link>
          </div>
          <p className="text-xs text-muted-foreground">
            © 2026 BidTrip · {language === 'en' ? 'Mexico' : 'México'}
          </p>
        </div>
      </footer>
    </div>
  );
}
